import { openDB, type IDBPDatabase } from 'idb';

/**
 * Durable device storage (spec §15.2, §9.3).
 *
 * All private data is namespaced by account so a second parent signing in on
 * the same device can never read or replay the first one's work (spec §15.3).
 * A nickname is never used as a key (spec §14.2).
 */

const DB_NAME = 'examine-local';
const DB_VERSION = 1;

export type StoreName =
  | 'children'
  | 'progress'
  | 'artworks'
  | 'revisions'
  | 'drafts'
  | 'blobs'
  | 'editions'
  | 'placements'
  | 'sessions'
  | 'outbox'
  | 'meta';

let dbp: Promise<IDBPDatabase> | null = null;

function db() {
  if (!dbp) {
    dbp = openDB(DB_NAME, DB_VERSION, {
      upgrade(d) {
        const stores: StoreName[] = [
          'children', 'progress', 'artworks', 'revisions', 'drafts',
          'blobs', 'editions', 'placements', 'sessions', 'outbox', 'meta',
        ];
        for (const s of stores) {
          if (!d.objectStoreNames.contains(s)) d.createObjectStore(s);
        }
      },
    });
  }
  return dbp;
}

/** Thrown when the device refuses to store — quota, private mode, etc. */
export class LocalWriteError extends Error {
  cause_?: unknown;
  reason: 'quota' | 'unavailable' | 'unknown';

  constructor(
    message: string,
    cause?: unknown,
    reason: 'quota' | 'unavailable' | 'unknown' = 'unknown',
  ) {
    super(message);
    this.name = 'LocalWriteError';
    this.cause_ = cause;
    this.reason = reason;
  }
}

function classify(err: unknown): 'quota' | 'unavailable' | 'unknown' {
  const name = (err as { name?: string } | null)?.name;
  if (name === 'QuotaExceededError') return 'quota';
  if (name === 'InvalidStateError' || name === 'SecurityError') return 'unavailable';
  return 'unknown';
}

/** Namespaced key. `account` is the parent account id, or 'guest' for /try. */
function k(account: string, id: string) {
  return `${account}::${id}`;
}

export const LocalStore = {
  async put<T>(store: StoreName, account: string, id: string, value: T): Promise<void> {
    try {
      const d = await db();
      await d.put(store, value, k(account, id));
    } catch (err) {
      throw new LocalWriteError(`Could not save to ${store}`, err, classify(err));
    }
  },

  async get<T>(store: StoreName, account: string, id: string): Promise<T | undefined> {
    try {
      const d = await db();
      return (await d.get(store, k(account, id))) as T | undefined;
    } catch {
      return undefined;
    }
  },

  async delete(store: StoreName, account: string, id: string): Promise<void> {
    try {
      const d = await db();
      await d.delete(store, k(account, id));
    } catch {
      /* deletion failure is non-fatal */
    }
  },

  /** All values in a store belonging to one account. */
  async list<T>(store: StoreName, account: string): Promise<T[]> {
    try {
      const d = await db();
      const keys = await d.getAllKeys(store);
      const prefix = `${account}::`;
      const mine = keys.filter((key) => typeof key === 'string' && key.startsWith(prefix));
      const out: T[] = [];
      for (const key of mine) out.push((await d.get(store, key)) as T);
      return out.filter(Boolean);
    } catch {
      return [];
    }
  },

  /** Removes every record belonging to one account (sign-out / deletion). */
  async clearAccount(account: string): Promise<void> {
    const d = await db();
    const prefix = `${account}::`;
    for (const store of d.objectStoreNames) {
      const keys = await d.getAllKeys(store);
      for (const key of keys) {
        if (typeof key === 'string' && key.startsWith(prefix)) await d.delete(store, key);
      }
    }
  },

  /**
   * Honest storage reporting for the parent Downloads screen (spec §12 P05).
   * Returns null when the browser will not tell us.
   */
  async estimate(): Promise<{ usage: number; quota: number } | null> {
    if (!navigator.storage?.estimate) return null;
    try {
      const e = await navigator.storage.estimate();
      if (e.usage == null || e.quota == null) return null;
      return { usage: e.usage, quota: e.quota };
    } catch {
      return null;
    }
  },

  /**
   * Ask the browser to make storage persistent so a draft is not silently
   * evicted. The answer is reported honestly — web storage is never claimed
   * to be as durable as a native download (spec §15.1).
   */
  async requestPersistence(): Promise<boolean> {
    if (!navigator.storage?.persist) return false;
    try {
      return await navigator.storage.persist();
    } catch {
      return false;
    }
  },
};

/** Blob helpers — real files, never giant base64 strings in a record (§9.3). */
export const BlobStore = {
  async put(account: string, id: string, blob: Blob) {
    await LocalStore.put('blobs', account, id, blob);
    return id;
  },
  async get(account: string, id: string): Promise<Blob | undefined> {
    return LocalStore.get<Blob>('blobs', account, id);
  },
  async url(account: string, id: string): Promise<string | null> {
    const b = await BlobStore.get(account, id);
    return b ? URL.createObjectURL(b) : null;
  },
  async delete(account: string, id: string) {
    await LocalStore.delete('blobs', account, id);
  },
};
